import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";

// Dark mode is only applied on authenticated app routes — public marketing
// pages, customer-facing accept/pay flows, etc. are not dark-mode aware.
const DARK_MODE_PATH_PREFIXES = ["/admin", "/employee", "/platform"];

const isDarkModeEligiblePath = (pathname) =>
  DARK_MODE_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const THEME_OPTIONS = ["light", "dark", "system"];
const STORAGE_KEY = "effitrack_theme";

const getSystemPrefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

const resolveEffective = (mode) => {
  if (mode === "system") return getSystemPrefersDark() ? "dark" : "light";
  return mode === "dark" ? "dark" : "light";
};

const applyThemeToDocument = (effective) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (effective === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  root.style.colorScheme = effective;
};

export function ThemeProvider({ children }) {
  const { currentUser } = useAuth();
  const location = useLocation();
  const eligible = isDarkModeEligiblePath(location.pathname);

  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return "system";
    const saved = localStorage.getItem(STORAGE_KEY);
    return THEME_OPTIONS.includes(saved) ? saved : "system";
  });

  const [effectiveTheme, setEffectiveTheme] = useState(() => resolveEffective(theme));

  // Apply theme class on mount and whenever theme or route changes.
  // Public routes always render in light mode regardless of preference.
  useEffect(() => {
    const effective = resolveEffective(theme);
    setEffectiveTheme(effective);
    applyThemeToDocument(eligible ? effective : "light");
  }, [theme, eligible]);

  // Listen to system preference changes when in "system" mode
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const effective = getSystemPrefersDark() ? "dark" : "light";
      setEffectiveTheme(effective);
      applyThemeToDocument(eligible ? effective : "light");
    };
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [theme, eligible]);

  // Load user's stored preference from Firestore on login
  useEffect(() => {
    const load = async () => {
      if (!currentUser?.uid) return;
      try {
        const snap = await getDoc(doc(db, "users", currentUser.uid));
        if (snap.exists()) {
          const stored = snap.data().theme;
          if (THEME_OPTIONS.includes(stored)) {
            setThemeState(stored);
            localStorage.setItem(STORAGE_KEY, stored);
          }
        }
      } catch (err) {
        console.error("Error loading theme preference:", err);
      }
    };
    load();
  }, [currentUser]);

  const setTheme = useCallback(
    async (next) => {
      if (!THEME_OPTIONS.includes(next)) return;
      setThemeState(next);
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {}
      if (currentUser?.uid) {
        try {
          await updateDoc(doc(db, "users", currentUser.uid), {
            theme: next,
            updatedAt: new Date().toISOString(),
          });
        } catch (err) {
          console.error("Error saving theme to Firestore:", err);
        }
      }
    },
    [currentUser]
  );

  const toggleTheme = useCallback(() => {
    setTheme(effectiveTheme === "dark" ? "light" : "dark");
  }, [effectiveTheme, setTheme]);

  const value = {
    theme,
    effectiveTheme,
    isDark: effectiveTheme === "dark",
    setTheme,
    toggleTheme,
    options: THEME_OPTIONS,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export default ThemeContext;
